import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Button as MuiButton } from '@material-ui/core';
import {
  FcPortraitMode,
  FcGlobe,
  FcOrgUnit,
  FcSurvey,
  FcAcceptDatabase,
  FcDataRecovery,
  FcTodoList,
  FcStatistics,
  FcPositiveDynamic,
} from 'react-icons/fc';
import { useHistory } from 'react-router-dom';
import CustomDropdownAlt from '../../components/selectors/CustomDropdownAlt';
import { useAuth } from '../../hooks/auth';
import Button from '../../components/buttons/Button';
import { faLogEvent } from '../../services/firebase/analytics';

import { useStyles } from './styles';

const Home: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const { user, selectedUnit, selectUnit } = useAuth();
  const [userUnits, setUserUnits] = useState<string[]>([]);
  const [openSelectUnit, setOpenSelectUnit] = useState(false);
  const [active, setActive] = useState('roads');
  const [enabledItems, setEnabledItems] = useState({
    staff: false,
    environment: false,
    structure: false,
    overview: true,
    orders: true,
    createOrder: true,
    availability: true,
    quality: false,
    results: false,
  });

  const handleSelectUnit = useCallback(
    (unitName: string) => {
      if (unitName.toLowerCase() === 'complexo de itabira') {
        selectUnit('itabira');
        return;
      }
      const selected = user.units.find(unit => unit.name === unitName);
      if (selected) {
        selectUnit(selected.id);
      }
    },
    [selectUnit, user.units],
  );

  const handleClickCreateOrder = (): void => {
    faLogEvent('CREATE_ORDER_FROM_INITIAL_SCREEN');
    history.push('/dashboard/monitoramento/ordens-de-servico', {
      shouldCreateOrder: true,
    });
  };

  const handleNavigate = (route: string): void => {
    if (route === 'createOrder') {
      handleClickCreateOrder();
      return;
    }
    history.push(`/dashboard/${route}`);
  };

  // SET SELECTED UNIT
  useEffect(() => {
    if (user.units.length > 0) {
      const unitsNames = user.units.map(unit => unit.name);
      setUserUnits(unitsNames);
    }
  }, [user.units]);

  useEffect(() => {
    if (active === 'roads') {
      setEnabledItems({
        staff: false,
        environment: false,
        structure: false,
        overview: true,
        orders: true,
        createOrder: true,
        availability: true,
        quality: false,
        results: false,
      });
    } else if (active === 'trucks') {
      setEnabledItems({
        staff: false,
        environment: false,
        structure: false,
        overview: false,
        orders: false,
        createOrder: false,
        availability: false,
        quality: false,
        results: false,
      });
    } else if (active === 'drills') {
      setEnabledItems({
        staff: true,
        environment: false,
        structure: false,
        overview: false,
        orders: false,
        createOrder: false,
        availability: false,
        quality: false,
        results: true,
      });
    }
  }, [active]);

  return (
    <div className={classes.container}>
      <header className={classes.header}>
        <CustomDropdownAlt
          open={openSelectUnit}
          text={selectedUnit.name}
          labels={['Complexo de Itabira', ...userUnits]}
          toggle={() => setOpenSelectUnit(!openSelectUnit)}
          onClickItem={(event: any) => handleSelectUnit(event.target.innerText)}
          height={48}
        />
      </header>
      <nav className={classes.nav}>
        <Button
          type="button"
          style={{ marginTop: 0 }}
          isSelected={active === 'roads'}
          onClick={() => setActive('roads')}
        >
          {t('components:nav.roads')}
        </Button>
        <Button
          type="button"
          style={{ marginTop: 0 }}
          isSelected={active === 'trucks'}
          onClick={() => setActive('trucks')}
        >
          {t('components:nav.trucks')}
        </Button>
        <Button
          type="button"
          style={{ marginTop: 0 }}
          isSelected={active === 'drills'}
          onClick={() => setActive('drills')}
        >
          {t('components:nav.drills')}
        </Button>
      </nav>
      <section className={classes.section}>
        <Typography variant="h2" className={classes.sectionTitle}>
          {t('routes:planejamento')}
        </Typography>
        <div className={classes.sectionButtons}>
          <MuiButton
            className={classes.button}
            disabled={!enabledItems.staff}
            startIcon={<FcPortraitMode size={32} />}
            onClick={() => handleNavigate('planejamento/efetivo')}
          >
            {t('routes:efetivo')}
          </MuiButton>
          <MuiButton
            className={classes.button}
            disabled={!enabledItems.environment}
            startIcon={<FcGlobe size={32} />}
            onClick={() => handleNavigate('planejamento/meio-ambiente')}
          >
            {t('routes:meio-ambiente')}
          </MuiButton>
          <MuiButton
            className={classes.button}
            disabled={!enabledItems.structure}
            startIcon={<FcOrgUnit size={32} />}
            onClick={() => handleNavigate('planejamento/estrutura')}
          >
            {t('routes:estrutura')}
          </MuiButton>
        </div>
      </section>
      <section className={classes.section}>
        <Typography variant="h2" className={classes.sectionTitle}>
          {t('routes:monitoramento')}
        </Typography>
        <div className={classes.sectionButtons}>
          <MuiButton
            className={classes.button}
            disabled={!enabledItems.overview}
            startIcon={<FcSurvey size={32} />}
            onClick={() => handleNavigate('monitoramento')}
          >
            {t('components:nav.overview')}
          </MuiButton>
          <MuiButton
            className={classes.button}
            disabled={!enabledItems.orders}
            startIcon={<FcAcceptDatabase size={32} />}
            onClick={() => handleNavigate('monitoramento/ordens-de-servico')}
          >
            {t('components:nav.orders')}
          </MuiButton>
          <MuiButton
            className={classes.button}
            disabled={!enabledItems.createOrder}
            startIcon={<FcDataRecovery size={32} />}
            onClick={() => handleNavigate('createOrder')}
          >
            {t('components:buttons.createOrder')}
          </MuiButton>
          <MuiButton
            className={classes.button}
            disabled={!enabledItems.availability}
            startIcon={<FcTodoList size={32} />}
            onClick={() => handleNavigate('monitoramento/disponibilidade')}
          >
            {t('components:nav.availability')}
          </MuiButton>
          <MuiButton
            className={classes.button}
            disabled={!enabledItems.quality}
            startIcon={<FcStatistics size={32} />}
            onClick={() => handleNavigate('monitoramento/qualidade')}
          >
            {t('components:nav.quality')}
          </MuiButton>
        </div>
      </section>
      <section className={classes.section}>
        <Typography variant="h2" className={classes.sectionTitle}>
          {t('routes:analise')}
        </Typography>
        <div className={classes.sectionButtons}>
          <MuiButton
            className={classes.button}
            disabled={!enabledItems.results}
            startIcon={<FcPositiveDynamic size={32} />}
            onClick={() => handleNavigate('analise/resultados')}
          >
            {t('components:nav.results')}
          </MuiButton>
        </div>
      </section>
    </div>
  );
};

export default Home;
